import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Lock, Database, FileCheck, Trash2, FileText, Mail, CheckCircle2, Eye } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export const Privacy = () => {
  const principles = [
    {
      icon: Shield,
      title: "GDPR Compliance",
      description: "European data protection standards"
    },
    {
      icon: FileCheck,
      title: "HIPAA Alignment",
      description: "Healthcare data privacy standards"
    },
    {
      icon: Lock,
      title: "UAE Personal Data Protection Law",
      description: "Local regulatory compliance"
    }
  ];

  const protectionMeasures = [
    "All data is stored in secure, encrypted environments",
    "Access is restricted to authorized personnel only",
    "Files are protected with passwords and controlled permissions",
    "No data is shared with third parties without explicit consent",
    "Regular security audits and compliance reviews"
  ];

  const anonymizationPractices = [
    "Removing personally identifiable information (PII)",
    "Using anonymized datasets for analysis",
    "Reporting only aggregated results",
    "Implementing data masking techniques",
    "Patient confidentiality maintained at all stages"
  ];

  const dataUsage = [
    {
      purpose: "Statistical Analysis",
      description: "Advanced analytics to extract meaningful insights from clinical data"
    },
    {
      purpose: "Reporting and Insights",
      description: "Creation of comprehensive reports and data visualizations"
    },
    {
      purpose: "Research Support",
      description: "Academic and clinical research methodology support (if agreed)"
    }
  ];

  const agreements = [
    {
      title: "Non-Disclosure Agreements (NDA)",
      description: "Legally binding confidentiality agreements for all projects"
    },
    {
      title: "Data Processing Agreements (DPA)",
      description: "Formal agreements defining data handling responsibilities (if required)"
    },
    {
      title: "Business Associate Agreements (BAA)",
      description: "HIPAA-compliant agreements for healthcare entities (when applicable)"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Helmet>
        <title>Privacy Policy - StatZen Analytics</title>
        <link rel="canonical" href="https://statzenanalytics.com/privacy" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm">
              <Shield className="h-12 w-12" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Data Privacy & Security Commitment
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            At <strong>StatZen Analytics</strong>, we understand the sensitivity of clinical and healthcare data. 
            We are committed to maintaining the highest standards of data privacy, confidentiality, 
            and security in all our services.
          </p>
        </div>
      </section>

      {/* Data Handling Principles */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-600 text-white px-4 py-2">
              Data Protection Standards
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Data Handling Principles
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We follow globally recognized data protection principles aligned with international standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {principles.map((principle, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <principle.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {principle.title}
                  </h3>
                  <p className="text-gray-600">
                    {principle.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How We Protect Your Data */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-blue-600 rounded-xl">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              How We Protect Your Data
            </h2>
          </div>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="space-y-4">
                {protectionMeasures.map((measure, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">{measure}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Data Anonymization */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-purple-600 rounded-xl">
              <Eye className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Data Anonymization
            </h2>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-8 border border-purple-200 mb-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              We prioritize patient confidentiality through comprehensive anonymization practices:
            </p>
            <div className="space-y-3">
              {anonymizationPractices.map((practice, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 bg-purple-600 rounded-full mt-3" />
                  <p className="text-gray-700">{practice}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
            <p className="text-gray-700 leading-relaxed">
              <strong>Important:</strong> All personally identifiable information is removed before analysis. 
              We work exclusively with de-identified datasets to ensure complete patient privacy and regulatory compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Data Usage */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-cyan-600 rounded-xl">
              <Database className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Data Usage
            </h2>
          </div>

          <p className="text-lg text-gray-700 mb-8">
            Client data is used <strong>strictly</strong> for agreed-upon purposes:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dataUsage.map((usage, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {usage.purpose}
                  </h3>
                  <p className="text-gray-600">
                    {usage.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-gray-700 leading-relaxed">
              <strong>No Secondary Use:</strong> Your data will never be used for purposes beyond the agreed scope 
              without explicit written consent. We do not sell, rent, or share client data with third parties.
            </p>
          </div>
        </div>
      </section>

      {/* Data Retention & Deletion */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-red-600 rounded-xl">
              <Trash2 className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Data Retention & Deletion
            </h2>
          </div>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Limited Retention Period</h3>
                    <p className="text-gray-700">
                      Data is retained <strong>only for the duration of the project</strong> unless otherwise agreed in writing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure Deletion</h3>
                    <p className="text-gray-700">
                      Upon project completion, data is <strong>securely deleted</strong> using industry-standard methods 
                      (overwriting, degaussing, or physical destruction for physical media).
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Return Option</h3>
                    <p className="text-gray-700">
                      Alternatively, data can be <strong>securely returned</strong> to the client in encrypted format 
                      and permanently deleted from our systems.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                    <span className="text-blue-600 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Deletion Certification</h3>
                    <p className="text-gray-700">
                      Upon request, we provide <strong>written certification</strong> confirming secure data deletion.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Confidentiality Assurance */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-green-600 rounded-xl">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Confidentiality Assurance
            </h2>
          </div>

          <p className="text-lg text-gray-700 mb-8">
            All projects are governed by comprehensive legal agreements:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {agreements.map((agreement, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <FileCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {agreement.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {agreement.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Your Rights
          </h2>

          <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 mb-6">
                As a client, you have the right to:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Request information about how your data is processed</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Request deletion of your data at any time</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Receive a copy of your data in portable format</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Withdraw consent for data processing</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Lodge a complaint with data protection authorities</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Request rectification of inaccurate data</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm">
              <Mail className="h-10 w-10" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Questions About Data Privacy?
          </h2>
          <p className="text-lg text-blue-100 mb-8 leading-relaxed">
            For any data privacy concerns, questions about our security practices, or to exercise your data rights, 
            please contact us:
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20 inline-block">
            <p className="text-lg mb-2">Email:</p>
            <a 
              href="mailto:info@statzenanalytics.com" 
              className="text-xl font-bold hover:text-blue-200 transition-colors"
            >
              info@statzenanalytics.com
            </a>
          </div>
          <p className="text-blue-100 mt-6">
            We typically respond to data privacy inquiries within 48 hours
          </p>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-gray-600">
            Last Updated: June 2026 | StatZen Analytics reviews and updates this privacy policy regularly to ensure compliance with evolving regulations
          </p>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
